/**
 * Shotgun-aware play order. A flight that starts on hole `startHole`
 * plays `startHole, startHole+1, …, 18, 1, 2, …, startHole-1`.
 *
 * `totalHoles` is the course length (always 18 in practice; passed in so
 * tests/fixtures can target 9-hole courses without touching this util).
 *
 * Returns a length-`totalHoles` array of hole numbers in the order the
 * flight plays them. `startHole` defaults to 1, which collapses the
 * sequence to the trivial `[1..18]` order.
 */
export const playSequence = (
  startHole: number | null | undefined,
  totalHoles: number = 18
): number[] => {
  const start =
    Number.isFinite(startHole) && startHole && startHole >= 1 ? startHole : 1
  return Array.from(
    { length: totalHoles },
    (_, i) => ((start - 1 + i) % totalHoles) + 1
  )
}

/**
 * Returns the next hole the flight should play after `currentHole`, or
 * `null` if `currentHole` is the final hole in the rotation. Wraps for
 * shotgun starts (after 18 → 1, until startHole - 1).
 */
export const nextInSequence = (
  startHole: number | null | undefined,
  currentHole: number,
  totalHoles: number = 18
): number | null => {
  const seq = playSequence(startHole, totalHoles)
  const i = seq.indexOf(currentHole)
  if (i < 0 || i === seq.length - 1) return null
  return seq[i + 1]
}

/**
 * "Next actionable hole" — like `nextInSequence` but with a sensible
 * fallback when you've reached the end of the rotation without all
 * holes scored. Walks the play sequence from `currentHole + 1`,
 * wrapping past the end, and returns the first hole where
 * `isComplete` returns false. Returns null only when EVERY hole is
 * complete (caller switches to submit).
 *
 * Why this exists: shotgun starts (e.g., startHole=5) make the
 * flight's last played hole `startHole - 1`. Before this helper, if a
 * captain hit their last hole with an earlier hole still missing, the
 * "Bekreft & gå til hull X" CTA dropped to plain "Bekreft" and
 * clicking it did nothing — the click handler had no fallback for the
 * end-of-rotation-but-not-finished case.
 */
export const nextActionableHole = (
  startHole: number | null | undefined,
  currentHole: number,
  totalHoles: number,
  isComplete: (hole: number) => boolean
): number | null => {
  const seq = playSequence(startHole, totalHoles)
  const startIdx = seq.indexOf(currentHole)
  // Walk the sequence starting AFTER current, wrapping past the end.
  // Step through every other hole exactly once (length - 1 steps).
  for (let step = 1; step < seq.length; step += 1) {
    const i = (startIdx + step) % seq.length
    const candidate = seq[i]
    if (!isComplete(candidate)) return candidate
  }
  // Every hole except possibly `currentHole` is complete — and the
  // caller's `canAdvance` only enables advance once currentHole is
  // complete too. So returning null here means the round is done.
  return null
}
